import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Interfaces — propiedades en camelCase (serialización .NET)
// ═══════════════════════════════════════════════════════════════

export interface FarmaciaLookup {
  id: number;
  _Name: string;
  provincia?: string;
  idFarmaciaCU2?: number;   // ID CU2 de la farmacia (vinculación GDO-T31)
  direccion?: string;
  codigoPostal?: string;
}

export interface ClienteLookup {
  id: number;
  _Name: string;
  codigoGF?: string;
}

// --- Ciclo de reconciliación ---

export interface ReconciliacionCiclo {
  id: number;
  timestamp: string;
  modo: string;
  opcion: string;
  farmaciasAnalizadas: number;
  totalOperaciones: number;
  totalExcepciones: number;
  totalInvariantes: number;
  fechaCreacion: string;
}

// --- Progreso del motor ---

export interface ReconciliacionProgreso {
  id: number;
  estado: number;              // 0=En curso, 1=Completado, 2=Error
  fase: number;                // 0-9
  faseTimestamp?: string;
  farmaciasProcesadas: number;
  farmaciasAnalizadas: number;
  totalFarmaciasAltas: number;
  totalFarmaciasBajas: number;
  totalFarmaceuticos: number;
  totalTransmisiones: number;
  totalEmpleadosAE: number;
  totalEmpleadosCE: number;
  totalTraslados: number;
  totalOperaciones: number;
  totalExcepciones: number;
  totalInvariantes: number;
  errorDetalle?: string;
}

export interface ReconciliacionLanzarRequest {
  modo: string;
  opcion: string;
  args: string;
}

export interface ReconciliacionResumen {
  reconciliacionId: number;
  timestamp: string;
  modo: string;
  farmaciasPorEstado: Record<number, number>;
  farmaceuticosPorEstado: Record<number, number>;
  transmisionesPorEstado: Record<number, number>;
  empleadosPorEstado: Record<number, number>;
  trasladosPorEstado: Record<number, number>;
  incidenciasPorEstado: Record<number, number>;
  totalFarmacias: number;
  totalFarmaceuticos: number;
  totalTransmisiones: number;
  totalEmpleados: number;
  totalTraslados: number;
  totalIncidencias: number;
}

// --- Farmacias (ámbito 1) ---

export interface ReconciliacionFarmacia {
  id: number;
  reconciliacionId: number;
  tipoOperacion: string;
  idFarmaciaCU2?: number;
  idFarmaciaGS?: number;
  licenciaSOE?: string;
  cif?: string;
  telefono?: string;
  direccion?: string;
  cp?: string;
  localidad?: string;
  provincia?: string;
  relacionesPendientes?: string;
  estadoId: number;
  notasGestion?: string;
  fechaCreacion: string;
  fechaModificacion?: string;
  modificadorId?: number;
  farmaciaGescom?: FarmaciaLookup;
}

// --- Farmacéuticos (ámbito 2) ---

export interface ReconciliacionFarmaceutico {
  id: number;
  reconciliacionId: number;
  idColegiadoCU2: number;
  idColegiadoGS?: number;           // ID del cliente en Auraquantic/Gescom (vinculado)
  nombre?: string;
  numColegiado?: string;
  colegio?: string;
  nif?: string;
  cp?: string;
  email?: string;
  rol?: string;
  relacionesPendientes?: string;
  estadoId: number;
  notasGestion?: string;
  fechaCreacion: string;
  fechaModificacion?: string;
  modificadorId?: number;
}

// --- Datos de cliente Auraquantic (para vinculación de farmacéuticos) ---

export interface AuraquanticClienteData {
  id: number;
  nombre?: string;
  numColegiado?: string;
  idColegiadoCU2?: number;           // id_colegiado de CU2 asignado en Gescom
  cp?: string;
  provincia?: string;
  nif?: string;
  email?: string;
}

// --- Transmisiones (ámbito 3 — F2, F3, F4) ---

export interface ReconciliacionTransmisionVendedor {
  transmisionId: number;
  vendedorId: number;
  fechaFin?: string;
  excepcionE2: boolean;
  resultado?: number;
  resultadoNotas?: string;
  cliente?: ClienteLookup;
}

export interface ReconciliacionTransmisionComprador {
  transmisionId: number;
  compradorId: number;
  fechaInicio?: string;
  excepcionE1: boolean;
  resultado?: number;
  resultadoNotas?: string;
  cliente?: ClienteLookup;
}

export interface ReconciliacionTransmision {
  id: number;
  reconciliacionId: number;
  farmaciaId: number;
  patron: string;
  fecha: string;
  estadoId: number;
  notasGestion?: string;
  notasProcesamiento?: string;
  infoProcesamiento?: string;
  fechaProcesamiento?: string;
  fechaCreacion: string;
  fechaModificacion?: string;
  modificadorId?: number;
  vendedores: ReconciliacionTransmisionVendedor[];
  compradores: ReconciliacionTransmisionComprador[];
  farmacia?: FarmaciaLookup;
  tieneIncidenciaI1: boolean;
  tieneExcepciones: boolean;
}

// --- Empleados (ámbito 3 — F1) ---

export interface ReconciliacionEmpleado {
  id: number;
  reconciliacionId: number;
  farmaciaId: number;
  farmaceuticoId: number;
  tipoOperacion: string;
  fecha?: string;
  estadoId: number;
  notasGestion?: string;
  resultado?: number;
  resultadoNotas?: string;
  notasProcesamiento?: string;
  fechaProcesamiento?: string;
  fechaCreacion: string;
  fechaModificacion?: string;
  modificadorId?: number;
  farmacia?: FarmaciaLookup;
  farmaceutico?: ClienteLookup;
}

// --- Traslados (ámbito 4) ---

export interface ReconciliacionTraslado {
  id: number;
  reconciliacionId: number;
  idTrasladoCU2: number;
  idFarmaciaCU2: number;
  idFarmaciaGS?: number;
  direccion?: string;
  cp?: string;
  localidad?: string;
  provincia?: string;
  direccionAnterior?: string;
  cpAnterior?: string;
  localidadAnterior?: string;
  provinciaAnterior?: string;
  estadoId: number;
  notasGestion?: string;
  notasProcesamiento?: string;
  fechaProcesamiento?: string;
  fechaCreacion: string;
  fechaModificacion?: string;
  modificadorId?: number;
  farmaciaGescom?: FarmaciaLookup;
}

// --- Incidencias (transversal) ---

export interface ReconciliacionIncidencia {
  id: number;
  reconciliacionId: number;
  farmaciaId: number;
  farmaceuticoId?: number;
  tipo: string;
  descripcion: string;
  estadoId: number;
  notasGestion?: string;
  fechaCreacion: string;
  fechaModificacion?: string;
  modificadorId?: number;
  farmacia?: FarmaciaLookup;
  farmaceutico?: ClienteLookup;
}

// --- Request ---

export interface UpdateReconciliacionEstadoRequest {
  estadoId: number;
  notasGestion?: string;
}

// ═══════════════════════════════════════════════════════════════
// Constantes
// ═══════════════════════════════════════════════════════════════

export const ESTADO_PENDIENTE = 0;
export const ESTADO_REALIZADO = 1;
export const ESTADO_DESCARTADO = 2;

export const INCIDENCIA_TOOLTIPS: Record<string, string> = {
  E1: 'Alta de titular bloqueado en Gescom (comprador)',
  E2: 'Baja de titular bloqueado en Gescom (vendedor)',
  I1: 'La farmacia quedaría sin titular con estos cambios',
  I2: 'Un farmacéutico no puede ser titular y empleado a la vez',
  F7: 'Anomalía F7 detectada por el motor',
};

export const INCIDENCIA_COLORS: Record<string, string> = {
  E1: 'red',
  E2: 'red',
  I1: 'purple',
  I2: 'purple',
  F7: 'volcano',
};

// ═══════════════════════════════════════════════════════════════
// Funciones API
// ═══════════════════════════════════════════════════════════════

// Helper para construir query string
const buildQuery = (params: Record<string, string | number | undefined>): string => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v!)}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
};

// --- Ciclos ---

export const getCiclos = async (): Promise<ReconciliacionCiclo[]> => {
  try {
    const response = await api.get('/reconciliacion/ciclos');
    return response.data;
  } catch (error) {
    console.error('Error fetching ciclos:', error);
    throw error;
  }
};

export const getCicloActual = async (): Promise<ReconciliacionCiclo> => {
  try {
    const response = await api.get('/reconciliacion/ciclos/actual');
    return response.data;
  } catch (error) {
    console.error('Error fetching ciclo actual:', error);
    throw error;
  }
};

export const getCicloById = async (id: number): Promise<ReconciliacionCiclo> => {
  try {
    const response = await api.get(`/reconciliacion/ciclos/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ciclo ${id}:`, error);
    throw error;
  }
};

export const getResumen = async (id: number): Promise<ReconciliacionResumen> => {
  try {
    const response = await api.get(`/reconciliacion/ciclos/${id}/resumen`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching resumen for ciclo ${id}:`, error);
    throw error;
  }
};

// --- Motor ---

export const lanzarMotor = async (modo: string, opcion: string = 'titulares', args: string = ''): Promise<{ ok: boolean; error?: string; reconciliacionId?: number }> => {
  try {
    const response = await api.post('/reconciliacion/motor/lanzar', { modo, opcion, args });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 409) {
      return error.response.data;
    }
    throw error;
  }
};

export const getMotorEnCurso = async (): Promise<{ id: number } | null> => {
  try {
    const response = await api.get('/reconciliacion/motor/en-curso');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    throw error;
  }
};

export const getProgreso = async (id: number): Promise<ReconciliacionProgreso> => {
  const response = await api.get(`/reconciliacion/ciclos/${id}/progreso`);
  return response.data;
};

// --- Farmacias ---

export const getFarmacias = async (reconciliacionId?: number, estadoId?: number): Promise<ReconciliacionFarmacia[]> => {
  try {
    const query = buildQuery({ reconciliacionId, estadoId });
    const response = await api.get(`/reconciliacion/farmacias${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching farmacias:', error);
    throw error;
  }
};

export const getFarmaciaById = async (id: number): Promise<ReconciliacionFarmacia> => {
  try {
    const response = await api.get(`/reconciliacion/farmacias/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching farmacia ${id}:`, error);
    throw error;
  }
};

export const updateFarmaciaEstado = async (id: number, estadoId: number, notasGestion?: string): Promise<void> => {
  try {
    await api.put(`/reconciliacion/farmacias/${id}/estado`, { estadoId, notasGestion });
  } catch (error) {
    console.error(`Error updating farmacia ${id}:`, error);
    throw error;
  }
};

// --- Farmacias: datos Gescom para comparación ---

export interface GescomFarmaciaData {
  id: number;
  idFarmaciaCU2?: number;
  direccion?: string;
  localidad?: string;
  cp?: string;
  provincia?: string;
  licenciaSOE?: number | string;
  telefono?: string;
}

export const getGescomFarmacia = async (farmaciaId: number): Promise<GescomFarmaciaData> => {
  try {
    const response = await api.get(`/reconciliacion/farmacias/gescom/${farmaciaId}`);
    const d = response.data;
    return {
      id: d.id_aq ?? d.id,
      idFarmaciaCU2: d.id_farmacia_CU2 ?? d.idFarmaciaCU2,
      direccion: d.direccion,
      localidad: d.poblacion ?? d.localidad,
      cp: d.cpostal ?? d.cp,
      provincia: d.provincia,
      licenciaSOE: d.licencia_SOE ?? d.licenciaSOE,
      telefono: d.telefono,
    };
  } catch (error) {
    console.error(`Error fetching Gescom farmacia ${farmaciaId}:`, error);
    throw error;
  }
};

export const vincularFarmacia = async (farmaciaRecId: number, auraquanticId: number): Promise<VincularClienteResponse> => {
  try {
    const response = await api.post(`/reconciliacion/farmacias/${farmaciaRecId}/vincular`, { auraquanticId });
    return response.data;
  } catch (error) {
    console.error(`Error vinculando farmacia ${farmaciaRecId}:`, error);
    throw error;
  }
};

// --- Farmacéuticos ---

export const getFarmaceuticos = async (reconciliacionId?: number, estadoId?: number): Promise<ReconciliacionFarmaceutico[]> => {
  try {
    const query = buildQuery({ reconciliacionId, estadoId });
    const response = await api.get(`/reconciliacion/farmaceuticos${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching farmaceuticos:', error);
    throw error;
  }
};

export const getFarmaceuticoById = async (id: number): Promise<ReconciliacionFarmaceutico> => {
  try {
    const response = await api.get(`/reconciliacion/farmaceuticos/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching farmaceutico ${id}:`, error);
    throw error;
  }
};

export const updateFarmaceuticoEstado = async (id: number, estadoId: number, notasGestion?: string): Promise<void> => {
  try {
    await api.put(`/reconciliacion/farmaceuticos/${id}/estado`, { estadoId, notasGestion });
  } catch (error) {
    console.error(`Error updating farmaceutico ${id}:`, error);
    throw error;
  }
};

// --- Farmacéuticos: vinculación con Auraquantic ---

export const getAuraquanticCliente = async (clienteId: number): Promise<AuraquanticClienteData> => {
  try {
    const response = await api.get(`/reconciliacion/farmaceuticos/gescom/${clienteId}`);
    const d = response.data;
    return {
      id: d.id,
      nombre: d.nombreCompleto,
      numColegiado: d.numColegiado,
      idColegiadoCU2: d.idColegiadoCU2,
      cp: d.cp,
      provincia: d.provincia,
      nif: d.nif,
      email: d.email,
    };
  } catch (error) {
    console.error(`Error fetching Gescom cliente ${clienteId}:`, error);
    throw error;
  }
};

export interface VincularClienteResponse {
  code: string;
  message: string;
}

export const vincularFarmaceutico = async (farmaceuticoRecId: number, auraquanticId: number): Promise<VincularClienteResponse> => {
  try {
    const response = await api.post(`/reconciliacion/farmaceuticos/${farmaceuticoRecId}/vincular`, { auraquanticId });
    return response.data;
  } catch (error) {
    console.error(`Error vinculando farmaceutico ${farmaceuticoRecId}:`, error);
    throw error;
  }
};

// --- Transmisiones ---

export const getRecTransmisiones = async (reconciliacionId?: number, estadoId?: number): Promise<ReconciliacionTransmision[]> => {
  try {
    const query = buildQuery({ reconciliacionId, estadoId });
    const response = await api.get(`/reconciliacion/transmisiones${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching rec transmisiones:', error);
    throw error;
  }
};

export const getRecTransmisionById = async (id: number): Promise<ReconciliacionTransmision> => {
  try {
    const response = await api.get(`/reconciliacion/transmisiones/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching rec transmision ${id}:`, error);
    throw error;
  }
};

export const updateRecTransmisionEstado = async (id: number, estadoId: number, notasGestion?: string): Promise<void> => {
  try {
    await api.put(`/reconciliacion/transmisiones/${id}/estado`, { estadoId, notasGestion });
  } catch (error) {
    console.error(`Error updating rec transmision ${id}:`, error);
    throw error;
  }
};

// --- Empleados ---

export const getRecEmpleados = async (reconciliacionId?: number, estadoId?: number): Promise<ReconciliacionEmpleado[]> => {
  try {
    const query = buildQuery({ reconciliacionId, estadoId });
    const response = await api.get(`/reconciliacion/empleados${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching rec empleados:', error);
    throw error;
  }
};

export const getRecEmpleadoById = async (id: number): Promise<ReconciliacionEmpleado> => {
  try {
    const response = await api.get(`/reconciliacion/empleados/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching rec empleado ${id}:`, error);
    throw error;
  }
};

export const updateRecEmpleadoEstado = async (id: number, estadoId: number, notasGestion?: string): Promise<void> => {
  try {
    await api.put(`/reconciliacion/empleados/${id}/estado`, { estadoId, notasGestion });
  } catch (error) {
    console.error(`Error updating rec empleado ${id}:`, error);
    throw error;
  }
};

// --- Traslados ---

export const getTraslados = async (reconciliacionId?: number, estadoId?: number): Promise<ReconciliacionTraslado[]> => {
  try {
    const query = buildQuery({ reconciliacionId, estadoId });
    const response = await api.get(`/reconciliacion/traslados${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching traslados:', error);
    throw error;
  }
};

export const getTrasladoById = async (id: number): Promise<ReconciliacionTraslado> => {
  try {
    const response = await api.get(`/reconciliacion/traslados/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching traslado ${id}:`, error);
    throw error;
  }
};

export const updateTrasladoEstado = async (id: number, estadoId: number, notasGestion?: string): Promise<void> => {
  try {
    await api.put(`/reconciliacion/traslados/${id}/estado`, { estadoId, notasGestion });
  } catch (error) {
    console.error(`Error updating traslado ${id}:`, error);
    throw error;
  }
};

export interface ProcesarTrasladoResponse {
  error: number;
  mensajeError: string;
  resultadoCasoLimpio: string;
}

export const procesarTraslado = async (id: number, usuario?: number): Promise<ProcesarTrasladoResponse> => {
  try {
    const query = usuario != null ? `?usuario=${usuario}` : '';
    const response = await api.post(`/reconciliacion/traslados/${id}/procesar${query}`);
    return response.data;
  } catch (error) {
    console.error(`Error processing traslado ${id}:`, error);
    throw error;
  }
};

// --- Procesamiento de transmisiones ---

export interface ProcesarResultadoTitular {
  clienteId: number;
  procesado: boolean;
  motivo: string;
  casosEspecialesTratados: string[];
}

export interface ProcesarTransmisionResponse {
  error: number;
  mensajeError: string;
  resultadoCasoLimpio: string;
  tipoIncidencia: string;
  notasTramitacion: string;
  resultadosEntrantes: ProcesarResultadoTitular[];
  resultadosSalientes: ProcesarResultadoTitular[];
}

export const procesarTransmision = async (id: number, forzarTitularidad = false): Promise<ProcesarTransmisionResponse> => {
  try {
    const query = forzarTitularidad ? '?forzarTitularidad=true' : '';
    const response = await api.post(`/reconciliacion/transmisiones/${id}/procesar${query}`);
    return response.data;
  } catch (error) {
    console.error(`Error processing transmision ${id}:`, error);
    throw error;
  }
};

// --- Procesamiento de empleados ---

export interface ProcesarResultadoEmpleado {
  farmaciaId: number;
  clienteId: number;
  tipoOperacion: string;
  procesado: boolean;
  motivo: string;
  casosEspecialesTratados: string[];
}

export interface ProcesarEmpleadoResponse {
  error: number;
  mensajeError: string;
  resultadoCasoLimpio: string;
  tipoIncidencia: string;
  resultado: ProcesarResultadoEmpleado;
}

export const procesarEmpleado = async (id: number): Promise<ProcesarEmpleadoResponse> => {
  try {
    const response = await api.post(`/reconciliacion/empleados/${id}/procesar`);
    return response.data;
  } catch (error) {
    console.error(`Error processing empleado ${id}:`, error);
    throw error;
  }
};

// --- Incidencias ---

export const getIncidencias = async (reconciliacionId?: number, estadoId?: number, tipo?: string): Promise<ReconciliacionIncidencia[]> => {
  try {
    const query = buildQuery({ reconciliacionId, estadoId, tipo });
    const response = await api.get(`/reconciliacion/incidencias${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching incidencias:', error);
    throw error;
  }
};

export const getIncidenciaById = async (id: number): Promise<ReconciliacionIncidencia> => {
  try {
    const response = await api.get(`/reconciliacion/incidencias/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching incidencia ${id}:`, error);
    throw error;
  }
};

export const updateIncidenciaEstado = async (id: number, estadoId: number, notasGestion?: string): Promise<void> => {
  try {
    await api.put(`/reconciliacion/incidencias/${id}/estado`, { estadoId, notasGestion });
  } catch (error) {
    console.error(`Error updating incidencia ${id}:`, error);
    throw error;
  }
};
